/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
  FC,
} from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme, ThemeMode } from '../theme';

/** What the user picked. `system` (auto) follows the OS and is the default. */
export type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeModeContextValue {
  /** The resolved mode actually applied to the theme. */
  mode: ThemeMode;
  /** The user's preference (light / dark / system). */
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  // Back-compat helpers (still used by the old single-toggle path).
  toggleThemeMode: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  preference: 'system',
  setPreference: () => {},
  toggleThemeMode: () => {},
  setThemeMode: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

const STORAGE_KEY = 'theme';

const getSystemMode = (): ThemeMode =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

/**
 * Preference resolution order:
 *   1. Explicit choice persisted in localStorage (`light` / `dark` / `system`).
 *   2. `system` (auto) as the default — it follows the OS colour scheme live.
 */
const getInitialPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    /* localStorage unavailable — fall through */
  }
  return 'system';
};

interface ThemeModeProviderProps {
  children: React.ReactNode;
}

export const ThemeModeProvider: FC<ThemeModeProviderProps> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    getInitialPreference
  );
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);

  // Track the OS colour scheme so `system` (auto) updates live.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
      return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) =>
      setSystemMode(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const mode: ThemeMode = preference === 'system' ? systemMode : preference;

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Keep the document in sync so native form controls / SCSS hooks follow mode.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    root.style.colorScheme = mode;
  }, [mode]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore persistence errors */
    }
  }, []);

  // Back-compat: setting an explicit mode is simply a light/dark preference.
  const setThemeMode = useCallback(
    (next: ThemeMode) => setPreference(next),
    [setPreference]
  );

  // Back-compat: flip the resolved mode to the opposite explicit mode.
  const toggleThemeMode = useCallback(
    () => setPreference(mode === 'light' ? 'dark' : 'light'),
    [mode, setPreference]
  );

  const value = useMemo(
    () => ({ mode, preference, setPreference, toggleThemeMode, setThemeMode }),
    [mode, preference, setPreference, toggleThemeMode, setThemeMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
